import React, { useState } from 'react';
import Icon from 'components/AppIcon';


function AllocationRow({ allocation }) {
  return (
    <div className="flex items-center gap-3 py-2 px-3 rounded-md" style={{ background: 'var(--color-muted)' }}>
      <Icon name="GitBranch" size={13} color="var(--color-muted-foreground)" strokeWidth={2} />
      <span className="text-xs text-muted-foreground font-mono flex-1" style={{ fontVariantNumeric: 'tabular-nums' }}>
        Lote {allocation?.batch_id?.slice(-8)?.toUpperCase()}
      </span>
      <span className="text-xs font-mono font-semibold text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>
        {parseFloat(allocation?.weight_allocated)?.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 3 })} t
      </span>
      {allocation?.batch_yield_actual != null && (
        <span className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
          yield {parseFloat(allocation?.batch_yield_actual)?.toFixed(1)}%
        </span>
      )}
    </div>
  );
}

function SaleRow({ sale, isExpanded, onToggle, onCancelSale, cancelling }) {
  const [confirmCancel, setConfirmCancel] = useState(false);
  const soldDate = sale?.sold_at ? new Date(sale?.sold_at) : null;
  const dateStr = soldDate?.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const timeStr = soldDate?.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

  const handleCancelClick = (e) => {
    e?.stopPropagation();
    setConfirmCancel(true);
  };

  const handleConfirm = (e) => {
    e?.stopPropagation();
    setConfirmCancel(false);
    onCancelSale?.(sale);
  };

  const handleAbort = (e) => {
    e?.stopPropagation();
    setConfirmCancel(false);
  };

  return (
    <div className="border-b border-border last:border-0">
      <div className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors">
        {/* Date */}
        <div className="flex-shrink-0 w-24">
          <p className="text-xs font-mono text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>{dateStr}</p>
          <p className="text-xs text-muted-foreground font-mono" style={{ fontVariantNumeric: 'tabular-nums' }}>{timeStr}</p>
        </div>
        {/* Metal/Category */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
            {sale?.category_name}
          </p>
          <p className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
            {sale?.metal_name}
          </p>
        </div>
        {/* Weight */}
        <div className="flex-shrink-0 text-right hidden sm:block">
          <p className="text-sm font-mono font-semibold text-foreground" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {parseFloat(sale?.weight_sold_t)?.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 3 })} t
          </p>
          <p className="text-xs text-muted-foreground font-mono" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {parseFloat(sale?.price_per_ton)?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €/t
          </p>
        </div>
        {/* Total */}
        <div className="flex-shrink-0 text-right hidden md:block w-28">
          <p className="text-sm font-mono font-bold" style={{ color: 'var(--color-success)', fontVariantNumeric: 'tabular-nums' }}>
            {parseFloat(sale?.total_value)?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €
          </p>
          {sale?.buyer_name && (
            <p className="text-xs text-muted-foreground truncate" style={{ fontFamily: '"IBM Plex Sans", sans-serif', maxWidth: '7rem' }}>
              {sale?.buyer_name}
            </p>
          )}
        </div>

        {/* Cancel / Undo button */}
        <div className="flex-shrink-0 flex items-center gap-1">
          {confirmCancel ? (
            <>
              <button
                onClick={handleConfirm}
                disabled={cancelling}
                className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold transition-colors"
                style={{ background: 'rgba(220,38,38,0.1)', color: '#dc2626', border: '1px solid rgba(220,38,38,0.25)', fontFamily: '"IBM Plex Sans", sans-serif' }}
                title="Confirmar cancelación"
              >
                {cancelling ? (
                  <Icon name="Loader2" size={12} color="#dc2626" strokeWidth={2} className="animate-spin" />
                ) : (
                  <Icon name="Check" size={12} color="#dc2626" strokeWidth={2.5} />
                )}
                <span className="hidden sm:inline">Confirmar</span>
              </button>
              <button
                onClick={handleAbort}
                disabled={cancelling}
                className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold transition-colors"
                style={{ background: 'var(--color-muted)', color: 'var(--color-muted-foreground)', border: '1px solid var(--color-border)', fontFamily: '"IBM Plex Sans", sans-serif' }}
                title="Mantener venta"
              >
                <Icon name="X" size={12} color="var(--color-muted-foreground)" strokeWidth={2.5} />
              </button>
            </>
          ) : (
            <button
              onClick={handleCancelClick}
              disabled={cancelling}
              className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium transition-colors hover:bg-red-50 dark:hover:bg-red-950/20"
              style={{ color: 'var(--color-muted-foreground)', border: '1px solid transparent', fontFamily: '"IBM Plex Sans", sans-serif' }}
              title="Deshacer / Cancelar venta"
            >
              <Icon name="RotateCcw" size={13} color="var(--color-muted-foreground)" strokeWidth={2} />
              <span className="hidden lg:inline">Deshacer</span>
            </button>
          )}
        </div>

        {/* Expand */}
        <button
          onClick={onToggle}
          className="flex-shrink-0 p-1.5 rounded-md hover:bg-muted transition-colors"
          title="Ver distribución de lotes"
        >
          <Icon
            name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            size={15}
            color="var(--color-muted-foreground)"
            strokeWidth={2}
          />
        </button>
      </div>

      {/* Expanded Allocations */}
      {isExpanded && (
        <div className="px-4 pb-3 space-y-1.5">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2" style={{ fontFamily: '"IBM Plex Sans", sans-serif', letterSpacing: '0.05em' }}>
            Distribución FIFO por lotes
          </p>
          {sale?.allocations?.length > 0 ? (
            sale?.allocations?.map(alloc => (
              <AllocationRow key={alloc?.id} allocation={alloc} />
            ))
          ) : (
            <p className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
              Sin datos de distribución disponibles.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function RecentSalesTable({ sales, loading, onCancelSale, cancellingId }) {
  const [expandedIds, setExpandedIds] = useState(new Set());

  const toggleExpand = (id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next?.has(id)) next?.delete(id);
      else next?.add(id);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="p-5 border-b border-border">
          <div className="h-5 w-40 bg-muted rounded animate-pulse" />
        </div>
        {[1, 2, 3]?.map(i => (
          <div key={i} className="flex items-center gap-4 p-4 border-b border-border last:border-0">
            <div className="h-4 w-20 bg-muted rounded animate-pulse" />
            <div className="h-4 w-32 bg-muted rounded animate-pulse" />
            <div className="h-4 w-16 bg-muted rounded animate-pulse ml-auto" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="p-5 border-b border-border flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif', fontSize: '0.9375rem' }}>
            Ventas Recientes
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
            Últimas {Math.min(sales?.length, 20)} ventas de stock agregado
          </p>
        </div>
        {sales?.length > 0 && (
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md" style={{ background: 'rgba(5,150,105,0.08)', border: '1px solid rgba(5,150,105,0.2)' }}>
            <Icon name="TrendingUp" size={12} color="var(--color-success)" strokeWidth={2.5} />
            <span className="text-xs font-semibold" style={{ color: 'var(--color-success)', fontFamily: '"IBM Plex Sans", sans-serif' }}>
              {sales?.length} venta{sales?.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>

      {sales?.length === 0 ? (
        <div className="p-10 text-center">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: 'rgba(99,102,241,0.08)' }}>
            <Icon name="Receipt" size={22} color="var(--color-accent)" strokeWidth={1.5} />
          </div>
          <p className="text-sm font-medium text-foreground mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Sin ventas registradas</p>
          <p className="text-xs text-muted-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
            Las ventas de stock aparecerán aquí una vez registradas.
          </p>
        </div>
      ) : (
        <>
          {/* Table Header */}
          <div className="hidden sm:flex items-center gap-3 px-4 py-2.5 border-b border-border" style={{ background: 'var(--color-muted)' }}>
            <div className="w-24 text-xs font-semibold text-muted-foreground uppercase tracking-wide" style={{ fontFamily: '"IBM Plex Sans", sans-serif', letterSpacing: '0.04em' }}>Fecha</div>
            <div className="flex-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide" style={{ fontFamily: '"IBM Plex Sans", sans-serif', letterSpacing: '0.04em' }}>Categoría</div>
            <div className="w-28 text-xs font-semibold text-muted-foreground uppercase tracking-wide text-right hidden sm:block" style={{ fontFamily: '"IBM Plex Sans", sans-serif', letterSpacing: '0.04em' }}>Peso / Precio</div>
            <div className="w-28 text-xs font-semibold text-muted-foreground uppercase tracking-wide text-right hidden md:block" style={{ fontFamily: '"IBM Plex Sans", sans-serif', letterSpacing: '0.04em' }}>Total</div>
            <div className="w-24 text-xs font-semibold text-muted-foreground uppercase tracking-wide text-right" style={{ fontFamily: '"IBM Plex Sans", sans-serif', letterSpacing: '0.04em' }}>Acción</div>
            <div className="w-8" />
          </div>
          <div>
            {sales?.map(sale => (
              <SaleRow
                key={sale?.id}
                sale={sale}
                isExpanded={expandedIds?.has(sale?.id)}
                onToggle={() => toggleExpand(sale?.id)}
                onCancelSale={onCancelSale}
                cancelling={cancellingId === sale?.id}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
